import React from "react";


const EFFECTIVE_DATE = "October 11, 2025";

const Card: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <div className="bg-white dark:bg-slate-800 border border-gray-100 dark:border-slate-700 rounded-2xl shadow-sm p-6">
    {children}
  </div>
);

export const TermsAndConditions: React.FC = () => {
  return (
    <div className="max-w-4xl mt-20 mb-20 mx-auto p-6 sm:p-8">
      <h1 className="text-3xl font-semibold mb-4">Terms & Conditions</h1>
      <p className="text-sm text-gray-600 mb-6">
        Effective date: <strong>{EFFECTIVE_DATE}</strong>
      </p>

      <Card>
        <section className="mb-6">
          <h2 className="text-xl font-medium mb-2">Introduction</h2>
          <p className="text-gray-700 leading-relaxed">
            Welcome to <strong>Fleeby</strong> ("we," "our," or "us"). These
            Terms & Conditions govern your access to and use of{" "}
            <a href="https://www.fleeby.in" className="underline">
              https://www.fleeby.in
            </a>{" "}
            (the "Website"), operated by Fleeby, a small online business based
            in India. By accessing or using our website, you agree to be bound
            by these Terms. If you do not agree, please do not use our site.
          </p>
        </section>

        <section className="mb-6">
          <h2 className="text-xl font-medium mb-2">Eligibility</h2>
          <p className="text-gray-700 leading-relaxed">
            By using this Website, you confirm that you are at least 18 years
            old or have the consent of a parent or legal guardian to use it. You
            must provide accurate and complete information when creating an
            account or placing an order.
          </p>
        </section>

        <section className="mb-6">
          <h2 className="text-xl font-medium mb-2">User accounts</h2>
          <p className="text-gray-700 leading-relaxed">
            Creating an account allows you to use features like Add to Cart,
            Wishlist, and Order tracking. You are responsible for maintaining
            the confidentiality of your account and password. Fleeby will not be
            responsible for unauthorized use of your account.
          </p>
          <p className="text-gray-700 leading-relaxed mt-2">
            You may not use another person’s account without permission. If you
            suspect any unauthorized access, please contact us immediately at{" "}
            <a href="mailto:info@fleeby.in" className="underline">
              info@fleeby.in
            </a>
            .
          </p>
        </section>

        <section className="mb-6">
          <h2 className="text-xl font-medium mb-2">
            Products and availability
          </h2>
          <p className="text-gray-700 leading-relaxed">
            We offer two types of products:
          </p>
          <ul className="list-disc ml-5 mt-2 text-gray-700 space-y-2">
            <li>
              <strong>Standard products:</strong> Ready-to-ship items usually
              delivered within 10 days.
            </li>
            <li>
              <strong>Custom resin art:</strong> Handmade products that take 1–2
              weeks for creation and additional delivery time based on the
              courier.
            </li>
          </ul>
          <p className="text-gray-700 leading-relaxed mt-2">
            All products are subject to availability. We reserve the right to
            modify or discontinue any product without prior notice.
          </p>
        </section>

        <section className="mb-6">
          <h2 className="text-xl font-medium mb-2">Pricing and payments</h2>
          <p className="text-gray-700 leading-relaxed">
            All prices on Fleeby are listed in Indian Rupees (INR) and are
            inclusive of applicable taxes unless stated otherwise. Payment for
            online orders is processed securely through{" "}
            <strong>Razorpay</strong>. For custom orders placed through
            WhatsApp, payments are accepted via <strong>UPI</strong> (QR or UPI
            ID).
          </p>
          <p className="text-gray-700 leading-relaxed mt-2">
            Fleeby does not store or have access to your card or UPI details. If
            a payment fails or is deducted without order confirmation, please
            contact us for assistance.
          </p>
        </section>

        <section className="mb-6">
          <h2 className="text-xl font-medium mb-2">Shipping</h2>
          <p className="text-gray-700 leading-relaxed">
            Orders are processed within 1–3 business days after payment
            confirmation. Standard product delivery is completed within 10 days,
            while custom resin art products require 1–2 weeks to make before
            dispatch. Currently, Fleeby ships only within{" "}
            <strong>Kerala, India</strong>.
          </p>
          <p className="text-gray-700 leading-relaxed mt-2">
            Delivery timelines depend on courier partners, and we are not liable
            for delays once an order is handed over to the courier. Tracking
            details will be shared when available.
          </p>
        </section>

        <section className="mb-6">
          <h2 className="text-xl font-medium mb-2">
            Order cancellation and returns
          </h2>
          <p className="text-gray-700 leading-relaxed">
            You may cancel your order within <strong>2 days</strong> (48 hours)
            from placing it. After that, cancellations are not allowed as
            processing or production begins promptly.
          </p>
          <p className="text-gray-700 leading-relaxed mt-2">
            Returns are accepted only for defective, damaged, or incorrect items
            with <strong>video proof recorded at delivery</strong>. Refunds or
            replacements will be processed once the claim is verified. Refund
            processing time is <strong>3–5 business days</strong>.
          </p>
          <p className="text-gray-700 leading-relaxed mt-2">
            Detailed rules are covered in our{" "}
            <a href="/cancellation-refund" className="underline">
              Cancellation & Refund Policy
            </a>{" "}
            and{" "}
            <a href="/shipping-policy" className="underline">
              Shipping Policy
            </a>
            .
          </p>
        </section>

        <section className="mb-6">
          <h2 className="text-xl font-medium mb-2">Intellectual property</h2>
          <p className="text-gray-700 leading-relaxed">
            All content on Fleeby — including logos, images, text, graphics, and
            design — is owned by Fleeby or its content suppliers. You may not
            copy, reproduce, distribute, or use any part of our content for
            commercial purposes without written permission.
          </p>
        </section>

        <section className="mb-6">
          <h2 className="text-xl font-medium mb-2">User conduct</h2>
          <ul className="list-disc ml-5 mt-2 text-gray-700 space-y-2">
            <li>
              Do not use the Website for unlawful purposes or to harm, harass,
              or impersonate others.
            </li>
            <li>Do not upload malicious code, spam, or disruptive content.</li>
            <li>
              Do not attempt to access restricted parts of the website or
              servers without authorization.
            </li>
          </ul>
        </section>

        <section className="mb-6">
          <h2 className="text-xl font-medium mb-2">Limitation of liability</h2>
          <p className="text-gray-700 leading-relaxed">
            Fleeby is not liable for indirect, incidental, or consequential
            damages arising from your use of our site or products. While we
            ensure product quality and secure payment handling, we cannot
            guarantee uninterrupted or error-free website operation.
          </p>
        </section>

        <section className="mb-6">
          <h2 className="text-xl font-medium mb-2">Third-party links</h2>
          <p className="text-gray-700 leading-relaxed">
            Fleeby may contain links to third-party websites (e.g., Razorpay,
            WhatsApp, Google Analytics). We are not responsible for the content,
            privacy, or practices of those sites. Visiting third-party links is
            at your own risk.
          </p>
        </section>

        <section className="mb-6">
          <h2 className="text-xl font-medium mb-2">Privacy</h2>
          <p className="text-gray-700 leading-relaxed">
            Your privacy is important to us. Please refer to our{" "}
            <a href="/privacy-policy" className="underline">
              Privacy Policy
            </a>{" "}
            for information on how we collect, use, and protect your personal
            data.
          </p>
        </section>

        <section className="mb-6">
          <h2 className="text-xl font-medium mb-2">Indemnification</h2>
          <p className="text-gray-700 leading-relaxed">
            You agree to indemnify and hold harmless Fleeby and its team from
            any claims, damages, or expenses (including legal fees) arising from
            your misuse of the Website, violation of these Terms, or
            infringement of any rights.
          </p>
        </section>

        <section className="mb-6">
          <h2 className="text-xl font-medium mb-2">Modifications</h2>
          <p className="text-gray-700 leading-relaxed">
            Fleeby reserves the right to update or modify these Terms &
            Conditions at any time. Changes will be effective immediately upon
            posting on this page, and the updated effective date will reflect
            the latest version.
          </p>
        </section>

        <section>
          <h2 className="text-xl font-medium mb-2">Contact information</h2>
          <p className="text-gray-700 mb-2">
            If you have questions about these Terms, contact us:
          </p>
          <div className="flex flex-col sm:flex-row sm:items-center gap-3">
            <div className="flex-1">
              <p className="text-sm text-gray-600">
                Email:{" "}
                <a href="mailto:info@fleeby.in" className="underline">
                  info@fleeby.in
                </a>
              </p>
              <p className="text-sm text-gray-600">
                Phone/Support:{" "}
                <a href="tel:+917778461609" className="underline">
                  +91 97784 61609
                </a>
              </p>
            </div>
            <div className="text-sm text-gray-500">
              We’re happy to help with any clarification.
            </div>
          </div>
        </section>
      </Card>

      <p className="text-xs text-gray-500 mt-4">
        Last updated: {EFFECTIVE_DATE}. By continuing to use Fleeby, you accept
        these Terms & Conditions.
      </p>
    </div>
  );
};

export default TermsAndConditions;
